/**
 * Native (Capacitor) bootstrap.
 *
 * Runs ONLY inside the native iOS/Android shell. On the web (browser, SSR,
 * Lovable preview) every function here is a no-op, so the website is completely
 * unaffected. All Capacitor plugins are imported dynamically inside the guard so
 * they never enter the web/SSR bundle's critical path.
 */

let started = false;

/** True only when running inside the Capacitor native shell. */
export async function isNativeApp(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  try {
    const { Capacitor } = await import("@capacitor/core");
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/**
 * Initialise native-only enhancements: status bar styling, splash screen hide,
 * and the Android hardware back button. Safe to call on web — it returns early.
 */
export async function initNativeApp(): Promise<void> {
  if (started) return;
  if (!(await isNativeApp())) return;
  started = true;

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    // Android: paint the status bar to match the brand background.
    try {
      await StatusBar.setBackgroundColor({ color: "#0b0b12" });
    } catch {
      /* iOS doesn't support setBackgroundColor — ignore. */
    }
  } catch {
    /* status bar plugin unavailable — ignore. */
  }

  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch {
    /* splash plugin unavailable — ignore. */
  }

  // Android hardware back button: go back in history, or minimise the app at the
  // root instead of leaving a blank screen.
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });

    // Deep links / Android App Links: when the OS hands the app a URL
    // (tapped from Google search results, an email, a shared link, or a
    // notification that opens the app cold), route the in-app webview to the
    // matching page instead of dumping the user on the home screen.
    App.addListener("appUrlOpen", ({ url }) => {
      routeDeepLink(url);
    });

    // Handle the case where the app was launched (cold start) from a link.
    try {
      const launch = await App.getLaunchUrl();
      if (launch?.url) routeDeepLink(launch.url);
    } catch {
      /* getLaunchUrl unavailable — ignore. */
    }
  } catch {
    /* app plugin unavailable — ignore. */
  }
}

/** Hosts we treat as "ours" — deep links to these resolve to in-app pages. */
const APP_LINK_HOSTS = ["digitallybaffled.com", "www.digitallybaffled.com"];

/**
 * Navigate the webview to the path encoded in an incoming deep link.
 *
 * The native shell already has the live site loaded (server.url mode), so for
 * our own hosts we only need to move to the right path. Foreign hosts are left
 * alone (they fall through to the system browser / OAuth flow as before).
 */
function routeDeepLink(rawUrl: string): void {
  if (typeof window === "undefined" || !rawUrl) return;
  let target: URL;
  try {
    target = new URL(rawUrl);
  } catch {
    return;
  }

  // Only handle http(s) links to our own domains here.
  if (!/^https?:$/.test(target.protocol)) return;
  if (!APP_LINK_HOSTS.includes(target.hostname)) return;

  const dest = `${target.pathname}${target.search}${target.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (dest === current) return;

  // Prefer a client-side (SPA) navigation if the router exposed one, so we keep
  // app state and avoid a full reload; otherwise fall back to a same-origin
  // location change which the SSR site handles cleanly.
  const navigate = (window as unknown as { __appNavigate?: (to: string) => void })
    .__appNavigate;
  if (typeof navigate === "function") {
    navigate(dest);
  } else {
    window.location.assign(dest);
  }
}
